/**
 * Qualified SDK command wrappers — exposed as ThryvQualified.Commands.
 *
 * Thin pass-throughs to the public commands exposed by qualified.js. The
 * qualified() stub queue is installed by the GTM Qualified tag — these
 * wrappers expect window.qualified to already be callable by the time
 * they run.
 *
 * Two groups:
 *   1. Documented commands     — listed in Qualified University client-side
 *                                API docs. Officially supported.
 *   2. Undocumented commands   — present in qualified.js but not in the
 *                                public docs. They work today but aren't
 *                                guaranteed across releases.
 */

const Commands = new Object();

// ---------------------------------------------------------------------------
// Stub / passthrough
// ---------------------------------------------------------------------------

/**
 * Generic passthrough to qualified(). Use named wrappers below where
 * available; reach for this only for commands without one.
 *
 * Documented public commands (from Qualified University):
 *   identify    page    open    showExperience
 *   handleEvents    handleMessengerEvents    getIdentity
 *
 * Additional commands found in qualified.js source but UNDOCUMENTED:
 *   teardown    setCookieConsent    setChatIsVisible    startCall
 *   endExperience    handleWidgetEvents    logDebugEvent
 *   registerForm    showForm    showFormExperience    showExperienceForForm
 *   saveFormData    saveSmartFormData    sendSmartFormData
 *   pardotFormSubmitted    emitFormFill    submitEmbeddedForm
 *   initPardotForm    initPardotFormThankYou
 *   parsePardotForm    parsePardotFormForLandingPage
 */
Commands.api = function () {
  window.qualified.apply(null, arguments);
};

// ---------------------------------------------------------------------------
// Documented public API
// ---------------------------------------------------------------------------

/**
 * Set visitor field values. Field API names must be registered in
 * Qualified admin (Settings → App Settings → Visitor Fields).
 *   Commands.identify({ email: 'x@y.com', country_code: 'US' })
 */
Commands.identify = function (traits) {
  this.api("identify", traits || {});
};

/** Emit a page view. Required on SPAs to re-evaluate experiences. */
Commands.page = function () {
  this.api("page");
};

/** Open the messenger window (programmatic equivalent of clicking the dock). */
Commands.open = function () {
  this.api("open");
};

/**
 * Trigger a specific manual experience by API name.
 *   Commands.showExperience('experience-123456')
 *   Commands.showExperience('experience-123456', true)  // start collapsed
 *
 * Find the API name in Qualified admin: Settings → Manual Experiences →
 * (experience) → API NAME field, top-right.
 */
Commands.showExperience = function (apiName, startCollapsed) {
  this.api("showExperience", apiName, startCollapsed === true);
};

/**
 * Retrieve current visitor field values via callback.
 *   Commands.getIdentity(function (data) { console.log(data); });
 *
 * Requires Qualified Success Architect to enable this on the account, and
 * only fields opted into "Send via API" in admin are returned.
 */
Commands.getIdentity = function (callback) {
  this.api("getIdentity", callback);
};

// ---------------------------------------------------------------------------
// UNDOCUMENTED — extracted from qualified.js source. Work today but are
// NOT in Qualified's public API docs. Could change or break in any future
// SDK release without notice. Use sparingly.
// ---------------------------------------------------------------------------

/** Tear down the widget entirely. Analogue of Intercom('shutdown'). */
Commands.teardown = function () {
  this.api("teardown");
};

/** Hide the chat panel. SDK stays loaded. */
Commands.hide = function () {
  this.api("setChatIsVisible", false);
};

/** Show the chat panel. */
Commands.show = function () {
  this.api("setChatIsVisible", true);
};

/** Close the currently running experience. SDK stays loaded. */
Commands.endExperience = function () {
  this.api("endExperience");
};

/** Toggle Qualified's persistence consent. Strictly coerced to boolean. */
Commands.setCookieConsent = function (granted) {
  this.api("setCookieConsent", granted === true);
};

export default Commands;
